/**
 * PalloniGol — quanto manca al prossimo gol (Christian 2026-11-03).
 *
 * Cinque palloni, uno per gol: quelli gia' segnati sono pieni, quello in
 * corso si riempie per la parte di strada fatta fra la soglia raggiunta e
 * la successiva (soglia spaccata del 2° gol = 2 palloni, a meta' fra 1° e
 * 2° = 1 pallone e mezzo). Prima del 1° gol si parte da una fascia larga
 * quanto la prima del regolamento (66 → 72 vuol dire che si parte da 60).
 */

import { useId } from "react";
import { tr } from "../../i18n";

const NUM_PALLONI = 5;

export interface SogliaGol {
  punteggio_minimo: string | number;
  gol_assegnati: number;
}

export interface ProgressoGol {
  gol: number;
  /** gol + frazione verso il prossimo, es. 1.5. */
  valore: number;
  /** Punti che mancano al prossimo gol; null se le soglie sono finite. */
  mancano: number | null;
}

export function progressoGol(totale: number, soglie: SogliaGol[]): ProgressoGol | null {
  // soglie[i] = punteggio minimo per fare i+1 gol
  const perGol = new Map<number, number>();
  for (const s of soglie) {
    if (s.gol_assegnati <= 0) continue;
    const p = Number(s.punteggio_minimo);
    const prima = perGol.get(s.gol_assegnati);
    if (prima === undefined || p < prima) perGol.set(s.gol_assegnati, p);
  }
  const t = [...perGol.entries()].sort((a, b) => a[0] - b[0]).map(([, p]) => p);
  if (t.length === 0) return null;

  let gol = 0;
  while (gol < t.length && totale >= t[gol]) gol++;
  if (gol >= t.length) return { gol, valore: gol, mancano: null };

  const larghezzaPrima = t.length > 1 ? t[1] - t[0] : 6;
  const da = gol > 0 ? t[gol - 1] : t[0] - larghezzaPrima;
  const a = t[gol];
  const frazione = Math.min(1, Math.max(0, (totale - da) / (a - da)));
  return { gol, valore: gol + frazione, mancano: Math.round((a - totale) * 10) / 10 };
}

function Pallone({ pieno }: { pieno: number }) {
  // pieno da 0 a 1: la parte colorata cresce da sinistra.
  return (
    // Il bagliore va sul pallone intero: dentro il ritaglio verrebbe squadrato.
    <span
      className={`relative inline-block w-6 h-6 ${
        pieno >= 1 ? "drop-shadow-[0_0_3px_rgb(var(--fs-accent)/0.9)]" : ""
      }`}
      aria-hidden="true"
    >
      <IconaPallone className="absolute inset-0 w-6 h-6 text-fg-muted/25" />
      {pieno > 0 && (
        <span
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${pieno * 100}%` }}
        >
          <IconaPallone className="w-6 h-6 max-w-none text-accent" />
        </span>
      )}
    </span>
  );
}

// Pallone classico: pentagono al centro, cinque esagoni chiusi dalle
// cuciture e cinque pentagoni che si affacciano dal bordo (tagliati dal cerchio).
const DIREZIONI = [0, 1, 2, 3, 4].map((k) => ((-90 + 72 * k) * Math.PI) / 180);
const pt = (r: number, a: number) =>
  `${(12 + r * Math.cos(a)).toFixed(2)},${(12 + r * Math.sin(a)).toFixed(2)}`;
const R_CENTRO = 3.6; // vertici del pentagono centrale
const R_PUNTA = 7.4; // punta interna dei pentagoni del bordo
const R_FIANCO = 9.7; // fianchi dei pentagoni del bordo
const APERTURA = (21 * Math.PI) / 180;
const PENTAGONO_CENTRO = DIREZIONI.map((a) => pt(R_CENTRO, a)).join(" ");
const PENTAGONI_BORDO = DIREZIONI.map((a) =>
  [
    pt(R_PUNTA, a),
    pt(R_FIANCO, a + APERTURA),
    pt(14, a + APERTURA * 1.3),
    pt(14, a - APERTURA * 1.3),
    pt(R_FIANCO, a - APERTURA),
  ].join(" "),
);
const CUCITURE =
  DIREZIONI.map((a) => `M${pt(R_CENTRO, a)}L${pt(R_PUNTA, a)}`).join("") +
  DIREZIONI.map(
    (a) => `M${pt(R_FIANCO, a + APERTURA)}L${pt(R_FIANCO, a + (2 * Math.PI) / 5 - APERTURA)}`,
  ).join("");

function IconaPallone({ className }: { className?: string }) {
  const clip = useId();
  const scuro = { fill: "rgb(var(--fs-bg-card))" };
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <defs>
        <clipPath id={clip}>
          <circle cx="12" cy="12" r="11" />
        </clipPath>
      </defs>
      <circle cx="12" cy="12" r="11" fill="currentColor" />
      <g clipPath={`url(#${clip})`}>
        <path
          d={CUCITURE}
          fill="none"
          style={{ stroke: "rgb(var(--fs-bg-card))" }}
          strokeWidth="0.9"
        />
        <polygon points={PENTAGONO_CENTRO} style={scuro} />
        {PENTAGONI_BORDO.map((p, i) => (
          <polygon key={i} points={p} style={scuro} />
        ))}
      </g>
      <circle
        cx="12"
        cy="12"
        r="11"
        fill="none"
        style={{ stroke: "rgb(var(--fs-bg-card))" }}
        strokeOpacity="0.6"
        strokeWidth="0.8"
      />
    </svg>
  );
}

export function PalloniGol({ progresso }: { progresso: ProgressoGol }) {
  const extra = progresso.gol > NUM_PALLONI ? progresso.gol - NUM_PALLONI : 0;
  const titolo =
    progresso.mancano === null
      ? tr("{{v1}} gol", { v1: progresso.gol })
      : tr("{{v1}} gol · mancano {{v2}} pt al prossimo", {
          v1: progresso.gol,
          v2: progresso.mancano.toLocaleString(),
        });
  return (
    <div className="flex flex-col items-center gap-0.5" title={titolo}>
      <div className="flex items-center gap-0.5" role="img" aria-label={titolo}>
        {Array.from({ length: NUM_PALLONI }, (_, i) => (
          <Pallone key={i} pieno={Math.min(1, Math.max(0, progresso.valore - i))} />
        ))}
        {extra > 0 && <span className="text-[10px] font-bold text-accent">+{extra}</span>}
      </div>
      {progresso.mancano !== null && (
        <div className="text-[9px] font-mono text-fg-muted">
          {tr("-{{v1}} pt al gol", { v1: progresso.mancano.toLocaleString() })}
        </div>
      )}
    </div>
  );
}
